import { Link } from '../../primitives/Link/Link';
import type { KeyboardEventHandler, MouseEventHandler, ReactNode, Ref } from 'react';
import { FilterChipCounter } from '../FilterChipCounter/FilterChipCounter';
import styles from './TabButton.module.css';

/**
 * Figma: `_Tab button base` (719:538) — one segment of the `Tabs` bar.
 *
 * The `_` prefix marks it internal, so it is not exported from the package;
 * `Tabs` is the surface a consumer touches. It lives as its own component for
 * the same reason `BreadcrumbButton` does — the set is drawn separately and
 * carries its own variant matrix.
 *
 * Four variant axes, of which two become props:
 *
 *   Current  False | True        -> `current`
 *   Size     md | sm             -> `size`
 *   State    Default | Hover     -> CSS, not a prop (see `state` below)
 *   Type     Button white        -> one option, so nothing to switch on
 *
 * `Type` has a single value in the set, the same shape `Breadcrumbs`' `State`
 * axis has, so it does not become a prop. The name is also a misnomer worth
 * knowing about: neither this component nor the `Tabs` container it sits in has
 * a stroke of any kind, despite the container's axis reading `Button border`.
 *
 * Geometry, read from the bindings rather than the rendered sizes:
 *
 *            height          padding              radius        counter
 *   md       24 (FIXED)      space/4 + space/12   radius/sm     yes
 *   sm       16 (HUG)        space/2 + space/8    radius/xs     no
 *
 * The md height is FIXED at 24 and its content — a 12px leading-none line box
 * inside 4px of padding — only comes to 20, so the height is set explicitly
 * rather than left to hug. sm hugs, and 2 + 12 + 2 lands on its 16 exactly.
 *
 * The counter is `_filter chip counter`, the same instance `FilterChip` uses, so
 * it renders through `FilterChipCounter` rather than being redrawn.
 *
 * COUNTER IS md-ONLY, and that is the design, not a shortcut. The `Badge`
 * boolean is declared on the whole set, but only the four md variants contain a
 * counter layer — the sm variants have no such node for the property to toggle.
 * The `Tabs` set then sets `Badge=true` on two *sm* instances ("Notifications"
 * and "Team"), where it draws nothing. `count` is therefore ignored at sm, which
 * matches what Figma renders. See plan.md §7 — the sm variants need the layer
 * added before this can change, and it would cost 2px of bar height.
 *
 * Typography is `Text sm/leading-none/Regular` (12/12, `letter-spacing/md`). It
 * is written out here rather than delegated to the `Text` atom because the
 * colour is a hover state, and `Text` takes colour as a prop — a prop cannot
 * respond to `:hover`.
 */
export type TabButtonSize = 'md' | 'sm';

/** Figma's `State` axis. */
export type TabButtonState = 'default' | 'hover';

export type TabButtonProps = {
  children: ReactNode;
  /** Figma `Current=True` — the selected segment. */
  current?: boolean;
  size?: TabButtonSize;
  /**
   * Figma's `Icon` boolean — the 12x12 slot before the label.
   *
   * A slot rather than an enum, the file's own reading: the layer is an
   * instance-swap (`🔀 Icon swap`), so the design says "some icon goes here"
   * and never which. Both variants bind the glyph to `icon/secondary`, which is
   * already `Icon`'s default, so nothing here needs to recolour it.
   *
   * Added 2026-09-21 for Quick search, whose two segments carry `search` and
   * `chat history`. Nothing drew this slot before, which is why it was not
   * built earlier.
   */
  icon?: ReactNode;
  /** Figma's `Badge` boolean. Rendered at `md` only — see the note above. */
  count?: number;
  /** Figma `Full width=True`: the segment fills its share of the bar. */
  fullWidth?: boolean;
  /**
   * Whether the selected segment paints its own white chip and shadow. `Tabs`
   * turns it off and paints one sliding chip behind the bar instead, so the
   * selection travels between segments rather than blinking from one to the
   * next. Standalone, the segment draws its Figma variant as before.
   */
  surface?: boolean;
  /** Renders an `<a>` instead of a `<button>`. */
  href?: string;
  onClick?: MouseEventHandler<HTMLElement>;
  onKeyDown?: KeyboardEventHandler<HTMLElement>;
  /** Force a state for documentation and visual regression only. */
  state?: TabButtonState;
  /*
   * Semantics supplied by `Tabs`. Not in Figma, which draws appearance only —
   * see the accessibility note on `Tabs`.
   */
  id?: string;
  role?: 'tab';
  tabIndex?: number;
  'aria-selected'?: boolean;
  'aria-controls'?: string;
  'aria-current'?: 'page';
  ref?: Ref<HTMLElement>;
  className?: string;
};

export function TabButton({
  children,
  current = false,
  size = 'md',
  icon,
  count,
  fullWidth = false,
  surface = true,
  href,
  onClick,
  onKeyDown,
  state,
  id,
  role,
  tabIndex,
  'aria-selected': ariaSelected,
  'aria-controls': ariaControls,
  'aria-current': ariaCurrent,
  ref,
  className,
}: TabButtonProps) {
  /*
   * `interactive` is applied only when no state is forced, so `:hover` is live
   * in an app and inert in a states story — the repo's usual pattern for making
   * a documented state and a real one coexist.
   */
  const classes = [
    styles.tab,
    styles[size],
    current ? styles.selected : styles.unselected,
    state === 'hover' ? styles.hover : null,
    state === undefined ? styles.interactive : null,
    fullWidth ? styles.fill : null,
    surface ? null : styles.bare,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const content = (
    <>
      {/*
       * Before the label, which is the order the Figma frame draws: icon, text,
       * counter. `aria-hidden` comes from `Icon`'s own default — the label
       * beside it already names the tab.
       */}
      {icon}
      <span className={styles.label}>{children}</span>
      {/*
       * Figma's `Badge` boolean, absent from every sm variant. `count` is
       * ignored at sm rather than being drawn at a size the design has no
       * layer for.
       */}
      {count !== undefined && size === 'md' && <FilterChipCounter count={count} />}
    </>
  );

  if (href) {
    return (
      <Link
        ref={ref as Ref<HTMLAnchorElement>}
        id={id}
        className={classes}
        href={href}
        onClick={onClick}
        onKeyDown={onKeyDown}
        role={role}
        tabIndex={tabIndex}
        aria-selected={ariaSelected}
        aria-controls={ariaControls}
        aria-current={ariaCurrent}
      >
        {content}
      </Link>
    );
  }

  return (
    <button
      ref={ref as Ref<HTMLButtonElement>}
      id={id}
      className={classes}
      type="button"
      onClick={onClick}
      onKeyDown={onKeyDown}
      role={role}
      tabIndex={tabIndex}
      aria-selected={ariaSelected}
      aria-controls={ariaControls}
      aria-current={ariaCurrent}
    >
      {content}
    </button>
  );
}
